/**
 * Client-side ceiling for the whole send/link pipeline (Firestore + Storage uploads + Callable).
 * Keep in sync with `sendSignRequestEmail` `timeoutSeconds` in Firebase (must be >= this value).
 */
export const SEND_EMAIL_FLOW_TIMEOUT_MS = 300_000

/**
 * Rough heuristic so we can warn users when uploads may take noticeably longer than a quick send.
 * Does not change limits — only UX messaging.
 */
/** Detect phones/tablets for lighter PDF compression before Storage upload. */
export function isLikelyMobileClient() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true
  // iPadOS 13+ desktop mode can report as Mac without "Mobile" in UA.
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true
  return false
}

/** True on phones/tablets or when the layout is in a narrow (mobile) viewport. */
export function shouldUseMobileUploadProfile() {
  if (isLikelyMobileClient()) return true
  if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)')?.matches) return true
  return false
}

/** Lets Safari paint between heavy canvas + upload work so the tab is less likely to throttle. */
export function yieldToMainThread() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
    else setTimeout(resolve, 0)
  })
}

export function getPdfUploadCompressionOptions() {
  const mobile = shouldUseMobileUploadProfile()
  return {
    maxDimension: mobile ? 1100 : 1600,
    quality: mobile ? 0.45 : 0.55,
  }
}

export function dataUrlToBlob(dataUrl) {
  const commaIdx = dataUrl.indexOf(',')
  if (commaIdx < 0) throw new Error('Invalid data URL')
  const mime = dataUrl.slice(0, commaIdx).match(/:(.*?);/)?.[1] || 'image/jpeg'
  const b64 = dataUrl.slice(commaIdx + 1)
  let bin
  try {
    bin = atob(b64)
  } catch {
    throw new Error('Invalid image data')
  }
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read image data'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Normalize a PDF page image (data URL, Storage path, or HTTPS URL) to a data URL safe for canvas export.
 */
export async function resolvePdfPageImageForUpload(page, pageIndex, { readStorageBlob } = {}) {
  const src = page?.imageDataUrl
  if (typeof src === 'string' && src.startsWith('data:image/')) return src

  if (page?.storagePath && typeof readStorageBlob === 'function') {
    try {
      const blob = await readStorageBlob(page.storagePath)
      return await blobToDataUrl(blob)
    } catch (e) {
      console.warn('resolvePdfPageImageForUpload: Storage read failed:', page.storagePath, e?.message || e)
    }
  }

  if (typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
    try {
      const res = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await blobToDataUrl(await res.blob())
    } catch (e) {
      console.warn('resolvePdfPageImageForUpload: fetch failed:', e?.message || e)
    }
  }

  throw new Error(
    `PDF page ${pageIndex + 1} could not be prepared for upload. Open Preview PDF, re-upload the PDF if needed, then try sending again.`,
  )
}

/**
 * Resize + JPEG-compress a page image before Firebase Storage upload (critical on mobile Safari).
 */
function compressDataUrlImageForUploadOnce(
  dataUrl,
  { maxDimension = 1400, quality = 0.55 } = {},
) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      let w = img.naturalWidth || img.width
      let h = img.naturalHeight || img.height
      if (!w || !h) {
        reject(new Error('Invalid image dimensions'))
        return
      }
      const scale = Math.min(1, maxDimension / Math.max(w, h))
      w = Math.max(1, Math.round(w * scale))
      h = Math.max(1, Math.round(h * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas not supported'))
        return
      }
      ctx.drawImage(img, 0, 0, w, h)
      resolve(canvas.toDataURL('image/jpeg', quality))
    }
    img.onerror = () => reject(new Error('Failed to load page image for upload'))
    if (typeof dataUrl === 'string' && (dataUrl.startsWith('http://') || dataUrl.startsWith('https://'))) {
      img.crossOrigin = 'anonymous'
    }
    img.src = dataUrl
  })
}

/**
 * Resize + JPEG-compress a page image before Firebase Storage upload (critical on mobile Safari).
 * Retries once with smaller dimensions if canvas/memory fails.
 */
export function compressDataUrlImageForUpload(dataUrl, options = {}) {
  const { maxDimension = 1400, quality = 0.55 } = options
  return compressDataUrlImageForUploadOnce(dataUrl, { maxDimension, quality }).catch(() =>
    compressDataUrlImageForUploadOnce(dataUrl, {
      maxDimension: Math.min(900, Math.round(maxDimension * 0.72)),
      quality: Math.min(0.42, quality * 0.85),
    }),
  )
}

/**
 * Upload all PDF page images once (shared across multiple recipients in one send).
 * uploadPageJpeg(path, jpegDataUrl) should upload via Blob when possible (Safari-friendly).
 */
export async function uploadFullPdfPagesToStorage({
  fullPdfPages,
  linkId,
  uploadPageJpeg,
  onPdfUploadProgress,
  readStorageBlob,
}) {
  if (!fullPdfPages?.length || !linkId) return []
  const compression = getPdfUploadCompressionOptions()
  const paths = []
  const total = fullPdfPages.length
  for (let i = 0; i < total; i++) {
    await yieldToMainThread()
    const safeDataUrl = await resolvePdfPageImageForUpload(fullPdfPages[i], i, { readStorageBlob })
    const jpegDataUrl = await compressDataUrlImageForUpload(safeDataUrl, compression)
    const path = `temporaryLinks/${linkId}/page-${i}.jpg`
    await uploadPageJpeg(path, jpegDataUrl)
    paths.push(path)
    if (typeof onPdfUploadProgress === 'function') onPdfUploadProgress(i + 1, total)
  }
  return paths
}

/** Best-effort screen wake lock during long sends (mobile Safari background throttling). */
export async function withOptionalScreenWakeLock(fn) {
  if (typeof document === 'undefined' || typeof fn !== 'function') return fn()

  let wakeLock = null
  const releaseWakeLock = async () => {
    try {
      await wakeLock?.release()
    } catch (_) {
      /* already released */
    }
    wakeLock = null
  }
  const acquireWakeLock = async () => {
    if (!navigator?.wakeLock?.request || document.visibilityState !== 'visible') return
    try {
      await releaseWakeLock()
      wakeLock = await navigator.wakeLock.request('screen')
    } catch (_) {
      wakeLock = null
    }
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') void acquireWakeLock()
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  await acquireWakeLock()
  try {
    return await fn()
  } finally {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    await releaseWakeLock()
  }
}

export function estimateHeavySubmitPayload({
  agreementSizeKb = 0,
  signingSectionBytes = 0,
  maxAgreementSizeKb = 100,
  signatureTextLimitKB = 50,
  fullPdfPageCount = 0,
  attachmentBase64Chars = 0,
}) {
  const maxKb = Math.max(40, Number(maxAgreementSizeKb) || 100)
  const limitBytes = Math.max(512, (Number(signatureTextLimitKB) || 50) * 1024)
  const agreeRatio = Number(agreementSizeKb) / maxKb
  const signRatio = Number(signingSectionBytes) / limitBytes
  const manyPdfPages = (Number(fullPdfPageCount) || 0) >= 6
  const largeAttachments = (Number(attachmentBase64Chars) || 0) >= 750_000
  return agreeRatio >= 0.32 || signRatio >= 0.35 || manyPdfPages || largeAttachments
}

/**
 * Races a promise against a timeout so Firestore retries cannot stall the UI indefinitely.
 */
export function withTimeout(promise, ms, timeoutMessage = 'The operation timed out. Please try again.') {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      reject(Object.assign(new Error(timeoutMessage), { code: 'client-timeout' }))
    }, ms)
    promise.then(
      (v) => {
        clearTimeout(id)
        resolve(v)
      },
      (e) => {
        clearTimeout(id)
        reject(e)
      }
    )
  })
}

const NETWORKISH = /(network|failed to fetch|fetch|offline|internet|dns|name not resolved|err_name|connection|unreachable)/i

/**
 * User-facing copy for Firestore / offline / timeout failures.
 */
/** True when the send/copy pipeline hit the client-side 5-minute ceiling. */
export function isSendFlowClientTimeout(err) {
  return err?.code === 'client-timeout'
}

export function friendlyFirestoreOrNetworkMessage(err, fallback) {
  const code = err?.code
  const msg = String(err?.message || '')
  if (
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    code === 'client-timeout' ||
    code === 'resource-exhausted' ||
    code === 'cancelled' ||
    NETWORKISH.test(msg)
  ) {
    return 'We could not reach the server. Check your internet connection and try again. If the problem continues, refresh the page.'
  }
  return fallback
}
